import { useState, useEffect } from 'react';
import api from '../api';
import { QUALITY_SERVICE_API_URL } from '../config';

const HISTORY_DAYS = 5;
const MAX_EXECUTIONS = 10;

export const ALL_PACKAGES = 'all packages';

// Cycle names carry the platform; automated results are reported per SDK package.
// Order matters: the first match wins, so "React Native iOS" must resolve to react native.
const PACKAGE_BY_PLATFORM = {
  'react native': '@sdk/react-native',
  android: '@sdk/android',
  ios: '@sdk/ios',
};

export function packageForCycle(cycleName = '') {
  const name = cycleName.toLowerCase();
  const platform = Object.keys(PACKAGE_BY_PLATFORM).find(p => name.includes(p));
  return platform ? PACKAGE_BY_PLATFORM[platform] : ALL_PACKAGES;
}

const isoDay = (d) => d.toISOString().slice(0, 10);

// The endpoint can take a minute, so requests live outside the component: they keep running
// when the component re-renders or unmounts, and their promise is reused by anyone asking
// for the same keys until it resolves (then the result is served from `cache`).
const cache = new Map();
const inFlight = new Map();

// Several automated tests can share a key, so their executions are merged by date.
function toHistoryByKey(tests) {
  const byKey = {};
  for (const test of tests) {
    if (!test.testCaseKey) continue;
    byKey[test.testCaseKey] = [...(byKey[test.testCaseKey] ?? []), ...(test.lastStatuses ?? [])];
  }
  for (const key of Object.keys(byKey)) {
    byKey[key] = byKey[key].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, MAX_EXECUTIONS);
  }
  return byKey;
}

function fetchHistory(keysId, packageName) {
  const cacheKey = `${packageName}|${keysId}`;
  if (!inFlight.has(cacheKey)) {
    const end = new Date();
    const start = new Date(end.getTime() - HISTORY_DAYS * 24 * 60 * 60 * 1000);
    const request = api.post(`${QUALITY_SERVICE_API_URL}/hub/test-history/day-results/collapsed`, {
      startDate: isoDay(start),
      endDate: isoDay(end),
      env: 'all environments',
      package: packageName,
      unit: 'all units',
      testCaseKeys: keysId.split(','),
    })
      .then(res => {
        const byKey = toHistoryByKey(res.data);
        cache.set(cacheKey, byKey);
        return byKey;
      })
      .finally(() => inFlight.delete(cacheKey));
    inFlight.set(cacheKey, request);
  }
  return inFlight.get(cacheKey);
}

// Returns { history, loading, error } where history is { [testCaseKey]: [{ status, date }] },
// newest first.
export default function useLastExecutions(testCaseKeys, packageName = ALL_PACKAGES) {
  const keysId = testCaseKeys.join(',');
  const cacheKey = `${packageName}|${keysId}`;
  const [state, setState] = useState({ cacheKey: null, history: {}, error: false });

  useEffect(() => {
    if (!keysId || cache.has(cacheKey)) return undefined;
    let active = true;
    fetchHistory(keysId, packageName)
      .then(history => { if (active) setState({ cacheKey, history, error: false }); })
      .catch(() => { if (active) setState({ cacheKey, history: {}, error: true }); });
    return () => { active = false; };
  }, [keysId, packageName, cacheKey]);

  if (!keysId) return { history: {}, loading: false, error: false };
  if (cache.has(cacheKey)) return { history: cache.get(cacheKey), loading: false, error: false };
  if (state.cacheKey === cacheKey) return { history: state.history, loading: false, error: state.error };
  return { history: {}, loading: true, error: false };
}
